import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { type Color, colors } from "../../shared/colors";
import { shuffleColors } from "./CircleMesh";

type Weight = 200 | 400 | 600 | 800 | 1000;

const tickerTimings = [40, 50, 70, 90, 120];
const tickerFrameCount = 5;
const mutingOpacity = 0.15;
const targetCircleSize = 110;
const weights: Weight[] = [400, 600, 800];

const colorfulPaletteOptions = (
  paletteWeights: Weight[],
  restrictedColors?: Color[]
) => {
  const colorList = restrictedColors ?? colors;
  return colorList.flatMap((c) =>
    paletteWeights.map((w) => `var(--${c}-${w})`)
  );
};

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return hash >>> 0;
}

const multiplier = 1103515245;
const increment = 12345;
const modulus = 0x7fffffff;

function seededRandom(seed: number) {
  return () => {
    seed = (seed * multiplier + increment) & modulus;
    return seed / modulus;
  };
}

type Ball = {
  cx: number;
  cy: number;
  r: number;
  color: string;
  tickerColors: string[];
  key: string;
  row: number;
  col: number;
};

type Square = {
  x: number;
  y: number;
  size: number;
  color: string;
  tickerColors: string[];
  key: string;
  adjacentCells: [number, number][];
};

// A trimmed version of InteractiveCircleMeshPage: the colored cluster trails the
// cursor and a fresh random palette is picked on each mount, but without the
// sounds, keyboard, ripples, and badge that the standalone page carries.
export function CreditsMesh() {
  const containerRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const seed = useRef(Math.random().toString());
  const restrictedColors = useRef(shuffleColors());
  const desaturateId = useId();
  const [tickerFrame, setTickerFrame] = useState(0);

  const mouseTarget = useRef({ x: 0.5, y: 0.5 });
  const focalLayers = useRef([
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: 0.5 },
  ]);
  const [focalSnapshot, setFocalSnapshot] = useState(focalLayers.current);
  const animating = useRef(false);

  useEffect(() => {
    if (tickerFrame >= tickerFrameCount) return;
    const delay = tickerTimings[tickerFrame] ?? 100;
    const timeout = setTimeout(() => setTickerFrame((f) => f + 1), delay);
    return () => clearTimeout(timeout);
  }, [tickerFrame]);

  useEffect(() => {
    let raf = 0;
    const lerpRates = [0.08, 0.04, 0.02];
    const tick = () => {
      const layers = focalLayers.current;
      const target = mouseTarget.current;
      let moved = false;
      for (let i = 0; i < 3; i++) {
        const layer = layers[i]!;
        const rate = lerpRates[i]!;
        const nx = layer.x + (target.x - layer.x) * rate;
        const ny = layer.y + (target.y - layer.y) * rate;
        if (
          Math.abs(nx - layer.x) > 0.0001 ||
          Math.abs(ny - layer.y) > 0.0001
        ) {
          moved = true;
        }
        layers[i] = { x: nx, y: ny };
      }
      if (moved) {
        setFocalSnapshot([...layers]);
        raf = requestAnimationFrame(tick);
      } else {
        animating.current = false;
      }
    };
    const handleMouseMove = (e: MouseEvent) => {
      mouseTarget.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
      if (!animating.current) {
        animating.current = true;
        raf = requestAnimationFrame(tick);
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!containerSize || containerSize.width === 0) {
    return (
      <svg
        ref={containerRef}
        width="100%"
        height="100%"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      />
    );
  }

  const random = seededRandom(hashString(seed.current));
  const perRowCount = Math.max(
    1,
    Math.round(containerSize.width / targetCircleSize)
  );
  const totalRows = Math.ceil(
    perRowCount * (containerSize.height / containerSize.width)
  );
  const totalCount = totalRows * perRowCount;

  const limitedPalette = colorfulPaletteOptions(
    weights,
    restrictedColors.current
  )
    .sort(() => random() - 0.5)
    .slice(0, totalCount);
  for (const w of weights) {
    if (!limitedPalette.some((c) => c.endsWith(`-${w})`))) {
      const color =
        restrictedColors.current[
          Math.floor(random() * restrictedColors.current.length)
        ]!;
      limitedPalette[Math.floor(random() * limitedPalette.length)] =
        `var(--${color}-${w})`;
    }
  }

  const getColor = () =>
    limitedPalette[Math.floor(random() * limitedPalette.length)]!;
  const getTickerColors = () => {
    const result: string[] = [];
    for (let i = 0; i < tickerFrameCount; i++) result.push(getColor());
    return result;
  };
  const getDisplayColor = (finalColor: string, tickerColors: string[]) => {
    if (tickerFrame >= tickerFrameCount) return finalColor;
    return tickerColors[tickerFrame] ?? finalColor;
  };

  const viewBoxWidth = perRowCount;
  const ballDiameter = 1;
  const radius = ballDiameter / 2;
  const viewBoxHeight = totalRows;

  const noiseGrid: number[][] = [];
  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row: number[] = [];
    for (let colIndex = 0; colIndex < perRowCount; colIndex++) {
      row.push((random() - 0.5) * 3);
    }
    noiseGrid.push(row);
  }

  const maxDist = Math.sqrt((perRowCount / 2) ** 2 + (totalRows / 2) ** 2);
  const muteThreshold = maxDist * 0.13;

  const getFocalPos = (row: number, col: number) => {
    const noise = noiseGrid[row]?.[col] ?? 0;
    const layerIndex = ((Math.abs(noise) * 1000) | 0) % 3;
    const layer = focalSnapshot[layerIndex]!;
    return {
      focalCol: layer.x * perRowCount,
      focalRow: (1 - layer.y) * totalRows,
    };
  };

  const isMuted = (row: number, col: number) => {
    const { focalCol, focalRow } = getFocalPos(row, col);
    const dx = col - focalCol;
    const dy = row - focalRow;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const noise = noiseGrid[row]?.[col] ?? 0;
    return dist + noise > muteThreshold;
  };

  const balls: Ball[][] = [];
  const squares: Square[] = [];
  const squareVisuals = new Map<
    string,
    { color: string; tickerColors: string[] }
  >();
  const getSquareVisuals = (gridRow: number, gridCol: number) => {
    const key = `${gridRow}-${gridCol}`;
    if (!squareVisuals.has(key)) {
      squareVisuals.set(key, {
        color: getColor(),
        tickerColors: getTickerColors(),
      });
    }
    return squareVisuals.get(key)!;
  };

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const y = viewBoxHeight - (rowIndex + 0.5) * ballDiameter;
    const rowBalls: Ball[] = [];
    for (let colIndex = 0; colIndex < perRowCount; colIndex++) {
      const x = (colIndex + 0.5) * ballDiameter;
      const hasTicker = random() < 0.5;
      const tickerColors = getTickerColors();
      rowBalls.push({
        cx: x,
        cy: y,
        r: radius,
        color: getColor(),
        tickerColors: hasTicker ? tickerColors : [],
        key: `${rowIndex}-${colIndex}`,
        row: rowIndex,
        col: colIndex,
      });
    }
    balls.push(rowBalls);
  }

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    for (let colIndex = 0; colIndex < perRowCount; colIndex++) {
      const ball = balls[rowIndex]![colIndex]!;
      const overlap = 0.01;
      const size = radius + overlap * 2;

      // Each square sits at a grid intersection between four circles; it should
      // only stay colored when all four of those circles are colored.
      const tl = getSquareVisuals(rowIndex + 1, colIndex);
      squares.push({
        x: ball.cx - radius - overlap,
        y: ball.cy - radius - overlap,
        size,
        color: tl.color,
        tickerColors: tl.tickerColors,
        key: `${rowIndex}-${colIndex}-tl`,
        adjacentCells: [
          [rowIndex, colIndex],
          [rowIndex, colIndex - 1],
          [rowIndex + 1, colIndex],
          [rowIndex + 1, colIndex - 1],
        ],
      });

      const tr = getSquareVisuals(rowIndex + 1, colIndex + 1);
      squares.push({
        x: ball.cx - overlap,
        y: ball.cy - radius - overlap,
        size,
        color: tr.color,
        tickerColors: tr.tickerColors,
        key: `${rowIndex}-${colIndex}-tr`,
        adjacentCells: [
          [rowIndex, colIndex],
          [rowIndex, colIndex + 1],
          [rowIndex + 1, colIndex],
          [rowIndex + 1, colIndex + 1],
        ],
      });

      const bl = getSquareVisuals(rowIndex, colIndex);
      squares.push({
        x: ball.cx - radius - overlap,
        y: ball.cy - overlap,
        size,
        color: bl.color,
        tickerColors: bl.tickerColors,
        key: `${rowIndex}-${colIndex}-bl`,
        adjacentCells: [
          [rowIndex, colIndex],
          [rowIndex, colIndex - 1],
          [rowIndex - 1, colIndex],
          [rowIndex - 1, colIndex - 1],
        ],
      });

      const br = getSquareVisuals(rowIndex, colIndex + 1);
      squares.push({
        x: ball.cx - overlap,
        y: ball.cy - overlap,
        size,
        color: br.color,
        tickerColors: br.tickerColors,
        key: `${rowIndex}-${colIndex}-br`,
        adjacentCells: [
          [rowIndex, colIndex],
          [rowIndex, colIndex + 1],
          [rowIndex - 1, colIndex],
          [rowIndex - 1, colIndex + 1],
        ],
      });
    }
  }

  const squareMuted = (square: Square) =>
    square.adjacentCells.some(([r, c]) => isMuted(r, c));

  const flatBalls = balls.flat();

  return (
    <svg
      ref={containerRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMax slice"
    >
      <defs>
        <filter id={desaturateId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            in="SourceGraphic"
            type="saturate"
            values="0"
            result="gray"
          />
          <feFlood
            floodColor="var(--surface-primary)"
            floodOpacity="1"
            result="tint"
          />
          <feComposite
            in="gray"
            in2="tint"
            operator="arithmetic"
            k1="0"
            k2={mutingOpacity}
            k3={1 - mutingOpacity}
            k4="0"
            result="mixed"
          />
          <feComposite in="mixed" in2="SourceAlpha" operator="in" />
        </filter>
      </defs>

      <g filter={`url(#${desaturateId})`}>
        {squares
          .filter((s) => squareMuted(s))
          .map((square) => (
            <rect
              key={square.key}
              x={square.x}
              y={square.y}
              width={square.size}
              height={square.size}
              fill={getDisplayColor(square.color, square.tickerColors)}
            />
          ))}
      </g>
      {squares
        .filter((s) => !squareMuted(s))
        .map((square) => (
          <rect
            key={square.key}
            x={square.x}
            y={square.y}
            width={square.size}
            height={square.size}
            fill={getDisplayColor(square.color, square.tickerColors)}
          />
        ))}
      <g filter={`url(#${desaturateId})`}>
        {flatBalls
          .filter((b) => isMuted(b.row, b.col))
          .map((ball) => (
            <circle
              key={ball.key}
              cx={ball.cx}
              cy={ball.cy}
              r={ball.r}
              fill={getDisplayColor(ball.color, ball.tickerColors)}
            />
          ))}
      </g>
      {flatBalls
        .filter((b) => !isMuted(b.row, b.col))
        .map((ball) => (
          <circle
            key={ball.key}
            cx={ball.cx}
            cy={ball.cy}
            r={ball.r}
            fill={getDisplayColor(ball.color, ball.tickerColors)}
          />
        ))}
    </svg>
  );
}
